import crypto from "crypto-js";
import cookies from "js-cookie";
import type { AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import appConfigs from "@/configs";
import checkCryptoIsEnabled from "./checkCryptoIsEnabled";

export async function encryptData(
  value: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  const cryptographySettings = appConfigs.cryptography;
  const isEncrypted = value?.headers.get("X-Nexus-IsEncrypted") === "true";

  if (
    value.url !== cryptographySettings.generationPath &&
    checkCryptoIsEnabled(value.url as string) &&
    !isEncrypted
  ) {
    let key: string | null | undefined = sessionStorage.getItem(cryptographySettings.keyName);
    let iv: string | null | undefined = sessionStorage.getItem(cryptographySettings.ivName);

    if (key) {
      value.headers.set(cryptographySettings.keyName, key);
    }

    if (iv) {
      value.headers.set(cryptographySettings.ivName, iv);
    }

    if (value.method === "post" || value.method === "put") {
      key = key || cookies.get(cryptographySettings.keyName);
      iv = iv || cookies.get(cryptographySettings.ivName);

      const encryptedRequest = await encrypt(key, iv, value.data);
      value.data = encryptedRequest;
      value.headers["Content-Type"] = "text/plain";
    }
  }

  return value;
}

export async function decryptData<Response extends AxiosResponse | undefined>(
  value: Response
): Promise<Response> {
  const cryptographySettings = appConfigs.cryptography;
  const responseHeaders = value?.headers as AxiosHeaders | undefined;
  const isEncrypted = responseHeaders?.get("X-Nexus-IsEncrypted") === "true";

  if (value && isEncrypted && checkCryptoIsEnabled(value.config.url as string)) {
    if (value.config.url === cryptographySettings.generationPath) {
      const key = responseHeaders?.get(cryptographySettings.keyName);
      const iv = responseHeaders?.get(cryptographySettings.ivName);

      if (key) {
        sessionStorage.setItem(cryptographySettings.keyName, key as string);
      }

      if (iv) {
        sessionStorage.setItem(cryptographySettings.ivName, iv as string);
      }
    }

    const key =
      sessionStorage.getItem(cryptographySettings.keyName) ||
      cookies.get(cryptographySettings.keyName);

    const iv =
      sessionStorage.getItem(cryptographySettings.ivName) ||
      cookies.get(cryptographySettings.ivName);

    const decryptedResponse = await decrypt(key, iv, value.data);
    value.data = decryptedResponse;
  }

  return value;
}

async function encrypt(
  key: string | null | undefined,
  iv: string | null | undefined,
  data: unknown
): Promise<string> {
  const parsedKey = crypto.enc.Base64.parse(key as string);
  const parsedIV = crypto.enc.Base64.parse(iv as string);
  const encrypted = crypto.AES.encrypt(JSON.stringify(data), parsedKey, {
    iv: parsedIV,
    mode: crypto.mode.CBC,
    padding: crypto.pad.Pkcs7,
  });
  const result = encrypted.toString();

  return result;
}

async function decrypt(
  key: string | null | undefined,
  iv: string | null | undefined,
  data: string
): Promise<unknown> {
  try {
    const parsedKey = crypto.enc.Base64.parse(key as string);
    const parsedIV = crypto.enc.Base64.parse(iv as string);
    const decrypted = crypto.AES.decrypt(data, parsedKey, {
      iv: parsedIV,
      mode: crypto.mode.CBC,
      padding: crypto.pad.Pkcs7,
    });
    const result = decrypted.toString(crypto.enc.Utf8);

    return result ? JSON.parse(result) : null;
  } catch (_error) {
    return null;
  }
}
